import React from "react";
import { ArrowUpRight, MessageCircle, BookOpen, Calendar } from "lucide-react";
import { useContent } from "@/contexts/ContentContext";
import { resolveCtaHref, isExternalCta } from "@/lib/cta";
import { trackCta } from "@/lib/analytics";

export const Hero = () => {
    const { content } = useContent();
    const h = content.hero;
    const contact = content.contact;

    // Helper to wire each CTA to the right behavior
    const primaryHref = h.primary_cta_url;
    const secondaryHref = resolveCtaHref(
        { cta_kind: h.secondary_cta_kind || "whatsapp", cta_url: "", title: h.secondary_cta_label },
        contact,
    );
    const tertiaryHref = h.tertiary_cta_url || "#books-resources";
    const quaternaryHref = resolveCtaHref(
        { cta_kind: h.quaternary_cta_kind || "whatsapp", cta_url: "", title: h.quaternary_cta_label },
        contact,
    );

    return (
        <section id="home" data-testid="hero-section" className="relative isolate overflow-hidden min-h-[88vh] flex items-center pt-24">
            <div className="absolute inset-0 -z-20">
                <img
                    src={h.background_image_url}
                    alt=""
                    className="w-full h-full object-cover"
                    onError={(e) => { e.target.style.display = "none"; }}
                />
            </div>
            <div className="absolute inset-0 -z-10 hero-overlay" />
            <div className="absolute inset-0 -z-10 opacity-[0.06] pointer-events-none">
                <div className="max-w-7xl mx-auto h-full grid grid-cols-6">
                    {Array.from({ length: 6 }).map((_, i) => (<div key={i} className="border-l border-white/60" />))}
                </div>
            </div>

            <div className="max-w-7xl mx-auto w-full px-6 md:px-10 py-20 lg:py-28">
                <div className="grid lg:grid-cols-12 gap-10 items-center">
                    <div className="lg:col-span-8">
                        <p data-testid="hero-eyebrow" className="label-eyebrow !text-teal-300 mb-6">{h.eyebrow}</p>
                        <h1 data-testid="hero-headline" className="font-display font-semibold text-white text-4xl sm:text-5xl lg:text-[64px] leading-[1.05] tracking-tight">
                            {h.headline_line1}
                            <br />
                            <span className="text-teal-300">{h.headline_line2}</span>
                        </h1>
                        <p data-testid="hero-subheadline" className="mt-7 max-w-2xl text-lg sm:text-xl text-slate-200/90 font-body font-light leading-relaxed">
                            {h.subheadline}
                        </p>
                        <p data-testid="hero-intro" className="mt-5 max-w-2xl text-[15px] text-slate-300/85 font-body leading-relaxed">
                            {h.intro}
                        </p>

                        <div className="mt-10 flex flex-wrap gap-3">
                            <a
                                href={primaryHref}
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid="hero-cta-explore-awsai"
                                onClick={() => trackCta("awsai_click", { source: "hero" })}
                                className="btn-base btn-primary"
                            >
                                {h.primary_cta_label}
                                <ArrowUpRight className="w-4 h-4" />
                            </a>
                            <a
                                href={secondaryHref}
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid="hero-cta-request-consultation"
                                onClick={() => trackCta("whatsapp_click", { source: "hero", label: "request_consultation" })}
                                className="btn-base btn-ghost-dark"
                            >
                                <Calendar className="w-4 h-4" />
                                {h.secondary_cta_label}
                            </a>
                            <a
                                href={tertiaryHref}
                                data-testid="hero-cta-view-books"
                                className="btn-base btn-ghost-dark"
                            >
                                <BookOpen className="w-4 h-4" />
                                {h.tertiary_cta_label}
                            </a>
                            <a
                                href={quaternaryHref}
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid="hero-cta-whatsapp"
                                onClick={() => trackCta("whatsapp_click", { source: "hero", label: "general_enquiry" })}
                                className="btn-base btn-ghost-dark"
                            >
                                <MessageCircle className="w-4 h-4" />
                                {h.quaternary_cta_label}
                            </a>
                        </div>
                    </div>

                    <div className="lg:col-span-4 hidden lg:block">
                        <div className="border border-white/15 bg-white/[0.04] backdrop-blur-sm p-7">
                            <p className="label-eyebrow !text-teal-300">{content.awsai.eyebrow}</p>
                            <h3 className="mt-3 font-display text-2xl font-semibold text-white">{content.awsai.title}</h3>
                            <p className="mt-3 text-[14px] text-slate-300 leading-relaxed">
                                A workflow stability and AI readiness assessment used across SMEs, universities, government agencies and professional services.
                            </p>
                            <div className="mt-6 grid grid-cols-2 gap-4">
                                {[
                                    { v: String(content.awsai.dimensions.length), l: "Readiness Dimensions" },
                                    { v: String(content.smart_work.stages.length), l: "SMART-WORK Stages" },
                                    { v: String((content.services.items || []).filter((s) => s.visible !== false).length), l: "Service Lines" },
                                    { v: `${(content.books.items || []).filter((b) => b.visible !== false).length}+`, l: "Knowledge Resources" },
                                ].map((s) => (
                                    <div key={s.l} className="border-t border-white/10 pt-3">
                                        <div className="font-display text-2xl font-semibold text-white">{s.v}</div>
                                        <div className="text-[11.5px] uppercase tracking-[0.15em] text-slate-400 mt-1">{s.l}</div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="absolute bottom-0 left-0 right-0 border-t border-white/10 bg-[#0A192F]/70 backdrop-blur-sm">
                <div className="max-w-7xl mx-auto px-6 md:px-10 py-4 flex flex-wrap items-center gap-x-8 gap-y-2 text-[12px] uppercase tracking-[0.18em] text-slate-300/80">
                    <span className="text-teal-300">Trusted across</span>
                    {(h.audience_strip || []).map((tag, i) => (
                        <React.Fragment key={tag + i}>
                            {i > 0 && <span className="text-slate-500">/</span>}
                            <span>{tag}</span>
                        </React.Fragment>
                    ))}
                </div>
            </div>
        </section>
    );
};

export default Hero;
